import React, { useEffect, useRef, useState } from 'react';
import {
  number, func
} from 'prop-types';
import { useViewportResize } from '../hooks/cabinetVisualizerHooks';
import { useViewportStore, useCabinetStore } from '../../../../store/ui';

export const Viewport = ({ zoom = 1, render, onWidthChange, onScrollChange }) => {
  const { width, setElementRef } = useViewportResize(1);
  const [scrollOffset, setScrollOffset] = useState({ scrollLeft: 0, scrollTop: 0 });
  const isManualZoom = useViewportStore((state) => !!state.viewportInfo?.manualZoom);

  const viewportRef = useRef(null);
  const combinedRef = (node) => {
    setElementRef(node);
    viewportRef.current = node;
  };
  useEffect(() => {
    if (viewportRef.current !== null) {
      setTimeout(() => {
        viewportRef.current.scrollLeft = 0;
        // Scroll to bottom to show floor, but leave room for tall cabinets above
        const scrollHeight = viewportRef.current.scrollHeight;
        const clientHeight = viewportRef.current.clientHeight;
        const maxScrollTop = scrollHeight - clientHeight;

        // Scroll to bottom, but ensure we can see content above
        viewportRef.current.scrollTop = maxScrollTop;
      }, 0);
    }
  }, [width]);

  useEffect(() => {
    const handleScroll = () => {
      if (viewportRef.current) {
        setScrollOffset({
          scrollLeft: viewportRef.current.scrollLeft,
          scrollTop: viewportRef.current.scrollTop,
        });
        if (onScrollChange) {
          onScrollChange({
            scrollLeft: viewportRef.current.scrollLeft,
            scrollTop: viewportRef.current.scrollTop,
          });
        }
      }
    };

    const viewportEl = viewportRef.current;
    if (viewportEl) {
      viewportEl.addEventListener('scroll', handleScroll);
      return () => viewportEl.removeEventListener('scroll', handleScroll);
    }
    return () => {};
  }, [onScrollChange]);

  // Notify parent of width changes
  useEffect(() => {
    if (onWidthChange) {
      onWidthChange(width);
    }
  }, [width, onWidthChange]);

  // Deselect cabinet when clicking/tapping empty space inside the viewport.
  // Uses a document-level listener to avoid putting onClick on a non-interactive <div>,
  // which would violate jsx-a11y/no-static-element-interactions and click-events-have-key-events.
  const setSelectedCabinet = useCabinetStore((state) => state.setSelectedCabinet);
  useEffect(() => {
    const handleClickOutsideCabinet = (event) => {
      const viewportEl = viewportRef.current;
      if (!viewportEl) return;

      // Only act on clicks/taps that land inside the viewport
      if (!viewportEl.contains(event.target)) return;

      // Don't deselect if the click landed on a cabinet or popover
      if (
        event.target.closest('.cabinet')
        || event.target.closest('.cabinet-image-popover')
      ) return;

      setSelectedCabinet(null);
    };

    document.addEventListener('mousedown', handleClickOutsideCabinet);
    document.addEventListener('touchstart', handleClickOutsideCabinet, { passive: true });

    return () => {
      document.removeEventListener('mousedown', handleClickOutsideCabinet);
      document.removeEventListener('touchstart', handleClickOutsideCabinet, { passive: true });
    };
  }, [setSelectedCabinet]);

  return (
    <div ref={combinedRef} className="viewport" style={{ overflow: isManualZoom ? 'auto' : 'hidden' }}>
      <div className="viewport-content">
        {render(width, scrollOffset)}
      </div>
    </div>
  );
};

Viewport.propTypes = {
  zoom: number,
  render: func,
  onWidthChange: func,
  onScrollChange: func
};

Viewport.defaultProps = {
  zoom: 1,
  render: () => undefined,
  onWidthChange: () => undefined,
  onScrollChange: () => undefined
};
